import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { css } from '/imports/ui/css';
import { strings } from '/imports/strings';
import { localStorageGet, localStorageSet } from '/imports/utils';
import { toastOnError } from '/imports/ui/utils';
import { Button } from '/imports/ui/Button';
import { DevToolSection } from '/imports/ui/DevToolSection';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { quickbooksOutflowSync } from '/imports/api/quickbooks';

const Styled = styled.div`
  .field { margin-bottom: 0.75rem; max-width: 46rem; }
  label { display: block; margin-bottom: 0.25rem; font-weight: 500; }
  .field .input, .field input, .field select { width: 100%; box-sizing: border-box; }
  .meta-row { color: ${css.grey3}; font-size: 0.8rem; margin-top: 0.25rem; }
  .buttons { display: flex; gap: 0.5rem; margin: 0.5rem 0 0.75rem; }
  .totals { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.5rem; font-weight: 500; }
  .changes { max-height: 24rem; overflow: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
  th, td { text-align: left; padding: 0.25rem 0.5rem; border-bottom: 1px solid ${css.grey7}; }
  td.num { text-align: right; }
  tr.set td:first-child { border-left: 3px solid ${css.green}; }
  tr.suggest2 td:first-child { border-left: 3px solid ${css.orange}; }
  tr.suggest3 td:first-child { border-left: 3px solid ${css.grey5}; }
  tr.noProject td:first-child { border-left: 3px solid ${css.red2}; }
`;

const STORAGE_KEY = 'quickbooks.outflowSync';
const environmentOptions = [
  { value: 'production', label: strings.Production() },
  { value: 'sandbox', label: strings.Sandbox() },
];

/** Dev tool: run the QuickBooks Bill->Use outflow sweep (dry or real) and show every line's tier. */
export const DevToolQuickbooksOutflowSync = () => {
  const [saved] = useState(() => {
    try {
      const value = typeof localStorage === 'undefined' ? null : localStorageGet(STORAGE_KEY);
      return value && typeof value === 'object' ? value as any : null;
    }
    catch (_) {
      return null;
    }
  });
  const [form, setForm] = useState({ accessToken: '', realmId: '', environment: 'production', ...saved });
  const [loading, setLoading] = useState('');
  const [result, setResult] = useState<any>();
  /**
   * Applies a change to the form and remembers it.
   * @param patch - the fields that changed
   */
  const set = (patch: any) => setForm((current: any) => {
    const next = { ...current, ...patch };
    try { localStorageSet(STORAGE_KEY, next); }
    catch (_) { /* the form still works without persistence */ }
    return next;
  });
  /**
   * Runs a sweep.
   * @param dryRun - report only, writing nothing
   */
  const run = (dryRun: boolean) => toastOnError(async () => {
    if (loading) return;
    setLoading(dryRun ? 'dry' : 'apply');
    setResult(undefined);
    try {
      const r = await quickbooksOutflowSync.call({
        dryRun,
        accessToken: form.accessToken || undefined,
        realmId: form.realmId || undefined,
        environment: form.environment,
      });
      setResult(r);
      if (!r.ok) toast.error(r.message || strings.Error());
      else toast.success(`${r.tier1} + ${r.tier2} + ${r.tier3}`);
    }
    finally {
      setLoading('');
    }
  })();
  const money = (n?: number) => (n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));
  return (
    <DevToolSection
      title={strings.QuickbooksOutflowSync()}
      description={strings.QuickbooksOutflowSyncHelp()}
    >
      <Styled>
        <FormField field="accessToken">
          <label>{strings.AccessToken()}</label>
          <Input value={form.accessToken} type="password" onChange={(v) => set({ accessToken: v || '' })} />
          <div className="meta-row">{strings.QuickbooksApiTokenHint()}</div>
        </FormField>
        <FormField field="realmId">
          <label>{strings.CompanyId()}</label>
          <Input value={form.realmId} onChange={(v) => set({ realmId: v || '' })} />
        </FormField>
        <FormField field="environment">
          <label>{strings.Environment()}</label>
          <Select value={form.environment} options={environmentOptions} onChange={(v) => set({ environment: v })} />
        </FormField>
        <div className="buttons">
          <Button className="secondary" onClick={() => run(true)} disabled={!!loading}>{loading === 'dry' ? strings.Sending() : strings.DryRun()}</Button>
          <Button onClick={() => run(false)} disabled={!!loading}>{loading === 'apply' ? strings.Sending() : strings.Apply()}</Button>
        </div>
        {
          result ?
            <div>
              <div className="totals">
                <span>{result.billsScanned} {strings.Bills()}</span>
                <span>{result.projectLines} {strings.ProjectLines()}</span>
                <span>T1 {result.tier1}</span>
                <span>T2 {result.tier2}</span>
                <span>T3 {result.tier3}</span>
                <span>{result.unchanged} {strings.Unchanged()}</span>
                <span>{result.noProject} {strings.NoProject()}</span>
              </div>
              <div className="changes">
                <table>
                  <thead><tr><th>{strings.RicNumber()}</th><th>{strings.Project()}</th><th>{strings.Vendor()}</th><th>{strings.Reference()}</th><th className="num">{strings.Amount()}</th><th className="num">{strings.Paid()}</th><th>{strings.Milestone()}</th><th>{strings.TierAction()}</th></tr></thead>
                  <tbody>
                    {
                      (result.changes || []).map((c: any, i: number) => (
                        <tr key={i} className={c.action}>
                          <td>{c.externalId}</td>
                          <td>{c.projectName}</td>
                          <td>{c.vendor || '—'}</td>
                          <td>{c.docNumber || '—'}</td>
                          <td className="num">{money(c.amount)}</td>
                          <td className="num">{money(c.paid)}</td>
                          <td>{c.milestoneKey || '—'}</td>
                          <td>{c.tier ? `T${c.tier} · ${c.action}` : c.action}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
            :
            null
        }
      </Styled>
    </DevToolSection>
  );
};
